import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AssignedWireType } from '@/types/wireTypes';
import { RecipientDetails } from '@/bundle/shared/components/RecipientDetails/RecipientDetails';
import { DocumentVerificationPassedDetails } from '@/bundle/shared/components/DocumentVerificationPassedDetails/DocumentVerificationPassedDetails';
import { getDocumentVerifiedRecipientFullName } from '@/bundle/_Recipient/_Details/ui/RecipientDocumentVerification/helpers';
import { WIRE_STATUS_DB } from '@/const/wire';
import {
  isCancelledWireStatus,
  isExecutedWireStatus,
  isRecipientInvitedWireStatus,
  isRecipientProxy,
  isRecipientProxyInvitedWireStatus,
} from '@/helpers/wireHelpers';
import { getRecipientTrustStampStatusByOrgAdmin } from '../../api/api';
import { UserDetailsWidget } from '@/bundle/shared/components/UserDetailsWidget/UserDetailsWidget';
import { DetailsEditButton } from '@/bundle/_OrgAdmin/shared/ui/DetailsEditButton/DetailsEditButton';
import { ReInvitationDetails } from '@/bundle/shared/components/UserDetailsWidget/ReInvitationDetails';
import { reinviteExecutor } from '@/bundle/_OrgAdmin/pages/OutboundWire/api/executorApi';
import { useState } from 'react';
import { showSnackbarResponseError } from '@/helpers/helpers';

type OutboundWireUserDetailsType = {
  wire: AssignedWireType;
  showExecutorEditFormIds: {
    [id: string]: boolean;
  };
  isShowRecipientEditForm: boolean;
  onEditExecutorForm: (id: string) => void;
  onEditRecipientForm: () => void;
};

export const OutboundWireUserDetails = ({
  wire,
  showExecutorEditFormIds,
  isShowRecipientEditForm,
  onEditRecipientForm,
  onEditExecutorForm,
}: OutboundWireUserDetailsType) => {
  const queryClient = useQueryClient();
  const outboundWireStatus = wire?.status;
  const isProxy = isRecipientProxy(wire);
  const [modalConfirmationState, setModalConfirmationState] = useState<Record<string, boolean>>({});

  const refetchOutboundWire = () => {
    queryClient.invalidateQueries({ queryKey: ['load_outbound_wire', wire?.id] });
  };

  const hasAssignedRecipient = isProxy ? wire?.assigned_proxy_user : wire?.assigned_recipient;
  const isRecipientProxyInvited = isRecipientProxyInvitedWireStatus(outboundWireStatus);
  const isRecipientInvited = isRecipientInvitedWireStatus(outboundWireStatus);
  const isCancelWire = isCancelledWireStatus(outboundWireStatus);
  const isExecutedWire = isExecutedWireStatus(outboundWireStatus);

  const shouldShowDocumentVerificationPassedDetails = !!(
    wire?.is_pd_provider_kyc_required &&
    wire?.view_payment_details_event_register &&
    wire?.status !== WIRE_STATUS_DB.RECIPIENT_ASSIGNED &&
    wire?.status !== WIRE_STATUS_DB.RECIPIENT_PROXY_ASSIGNED
  );

  const { data: trustStampStatusData } = useQuery({
    queryKey: ['load_trust_stamp_status_by_org_admin', wire?.id],
    queryFn: () => getRecipientTrustStampStatusByOrgAdmin(wire?.id),
    enabled: shouldShowDocumentVerificationPassedDetails,
  });

  const { mutate: reinviteExecutorMutate } = useMutation({
    mutationKey: ['reinvite_executor'],
    mutationFn: (executorId: string) => {
      return reinviteExecutor(wire?.id, executorId);
    },
    onSuccess(response) {
      if (response?.error) {
        showSnackbarResponseError(response?.error);
      }

      refetchOutboundWire();
      setModalConfirmationState({});
    },
  });
  const documentVerifiedRecipientFullName = getDocumentVerifiedRecipientFullName(trustStampStatusData?.body);

  const hasExecutors = !!wire?.assigned_executors?.length;
  const executors = wire?.assigned_executors || [];

  return (
    <>
      {hasAssignedRecipient && !isShowRecipientEditForm && (
        <RecipientDetails
          wire={wire}
          isRecipientEdit={isProxy ? isRecipientProxyInvited : isRecipientInvited}
          onEditRecipient={onEditRecipientForm}
        />
      )}

      {shouldShowDocumentVerificationPassedDetails && (
        <DocumentVerificationPassedDetails fullName={documentVerifiedRecipientFullName} />
      )}

      {hasExecutors &&
        executors.map((executor, index) => {
          const header = `Executor ${++index}`;
          const executorUser = executor.user;
          const canEditExecutor = !!executorUser?.invitation_data && !isCancelWire && !isExecutedWire;

          const isVisibleExecutorDetails = !showExecutorEditFormIds[executor.id];

          if (!isVisibleExecutorDetails) return null;

          return (
            <UserDetailsWidget
              key={executor.id}
              header={header}
              user={executorUser}
              action={canEditExecutor && <DetailsEditButton onEdit={() => onEditExecutorForm(executor.id)} />}
              hasInvitedNotification={canEditExecutor}
            >
              {canEditExecutor && (
                <ReInvitationDetails
                  invitationDetails={executorUser?.invitation_data}
                  onOpen={() => {
                    setModalConfirmationState((state) => ({ ...state, [executor?.id]: true }));
                  }}
                  isOpen={modalConfirmationState[executor.id]}
                  onRefetchWire={refetchOutboundWire}
                  onConfirm={() => reinviteExecutorMutate(executor.id)}
                  onClose={() => setModalConfirmationState({})}
                />
              )}
            </UserDetailsWidget>
          );
        })}
    </>
  );
};
